import React from "react";
import { useQuery } from "@tanstack/react-query";
import { MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { useSystemSettings } from "../lib/systemSettings";

// Offizielle Brand-Icons via lucide-react (Instagram/Facebook) plus
// WhatsApp als SVG-Path. Farben nur bei den runden Icon-Buttons einsetzen —
// der Rest folgt der globalen Akzent-/Neutralfarben-Palette.
const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.52 3.48A11.94 11.94 0 0 0 12 0C5.37 0 .01 5.36.01 12c0 2.12.56 4.19 1.62 6.02L0 24l6.16-1.61A11.98 11.98 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.21-3.48-8.52ZM12 21.82c-1.9 0-3.75-.51-5.36-1.47l-.38-.22-3.66.96.98-3.57-.25-.37A9.83 9.83 0 0 1 2.18 12c0-5.42 4.41-9.82 9.82-9.82 2.62 0 5.09 1.02 6.94 2.88a9.75 9.75 0 0 1 2.88 6.94c0 5.42-4.4 9.82-9.82 9.82Zm5.4-7.35c-.3-.15-1.75-.87-2.02-.97-.27-.1-.47-.15-.67.15s-.77.97-.94 1.17c-.17.2-.35.22-.65.07-.3-.15-1.26-.47-2.4-1.49-.88-.79-1.48-1.77-1.65-2.07-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.09 4.49.71.31 1.27.5 1.7.64.71.23 1.36.2 1.87.12.57-.09 1.75-.71 2-1.4.25-.7.25-1.29.17-1.41-.08-.13-.27-.2-.57-.35Z"/>
  </svg>
);

const InstagramIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.32 4.14.6a5.9 5.9 0 0 0-2.13 1.39A5.9 5.9 0 0 0 .6 4.14c-.28.76-.47 1.64-.53 2.91C.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.25 2.15.53 2.91a5.9 5.9 0 0 0 1.39 2.13 5.9 5.9 0 0 0 2.13 1.39c.76.28 1.64.47 2.91.53C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.25 2.91-.53a5.9 5.9 0 0 0 2.13-1.39 5.9 5.9 0 0 0 1.39-2.13c.28-.76.47-1.64.53-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.25-2.15-.53-2.91a5.9 5.9 0 0 0-1.39-2.13A5.9 5.9 0 0 0 19.86.6c-.76-.28-1.64-.47-2.91-.53C15.67.01 15.26 0 12 0Zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32ZM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm6.4-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88Z"/>
  </svg>
);

const FacebookIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M24 12a12 12 0 1 0-13.88 11.85V15.47H7.08V12h3.04V9.36c0-3 1.79-4.66 4.53-4.66 1.31 0 2.68.23 2.68.23v2.95h-1.51c-1.49 0-1.96.93-1.96 1.87V12h3.33l-.53 3.47h-2.8v8.38A12 12 0 0 0 24 12Z"/>
  </svg>
);

/**
 * Community-/Social-Section.
 *
 * Wird bewusst NUR auf der Portal-Übersicht (``PortalHome``) gerendert,
 * damit das Portal auf den restlichen Tabs kompakt bleibt.
 * Layout, Breite und Padding orientieren sich am umliegenden Content
 * (dieselbe ``space-y``-Skala, gleiche Border/Rundung wie die anderen Cards).
 */
export default function PortalCommunitySection() {
  const { brandShort } = useSystemSettings();
  const q = useQuery({
    queryKey: ["portal-settings"],
    queryFn: async () => (await api.get("/portal/settings")).data,
  });
  const s = q.data || {};
  const wa = s.whatsapp_url;
  const ig = s.instagram_url;
  const fb = s.facebook_url;
  if (!wa && !ig && !fb) return null;
  return (
    <div className="space-y-3" data-testid="portal-community">
      <div className="text-xs uppercase tracking-widest text-cyan-400 truncate">
        {brandShort} auf Social Media
      </div>
      <div className="grid gap-3">
        {wa && (
          <a href={wa} target="_blank" rel="noopener noreferrer"
             data-testid="portal-whatsapp-link"
             className="flex items-center gap-3 border border-zinc-800 rounded-lg p-4 bg-zinc-900/70 hover:border-emerald-400/60 transition-colors">
            <span className="w-9 h-9 rounded-full flex items-center justify-center bg-[#25D366] text-white shrink-0">
              <WhatsAppIcon size={20} />
            </span>
            <div className="min-w-0">
              <div className="font-black uppercase tracking-wide text-sm truncate">{brandShort} Community · WhatsApp</div>
              <div className="text-xs text-zinc-500">Neuigkeiten, Support &amp; Störungsmeldungen</div>
            </div>
          </a>
        )}
        {ig && (
          <a href={ig} target="_blank" rel="noopener noreferrer"
             data-testid="portal-instagram-link"
             className="flex items-center gap-3 border border-zinc-800 rounded-lg p-4 bg-zinc-900/70 hover:border-cyan-400 transition-colors">
            <span className="w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0"
                  style={{ background: "linear-gradient(45deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5)" }}>
              <InstagramIcon size={20} />
            </span>
            <div className="min-w-0">
              <div className="font-black uppercase tracking-wide text-sm truncate">Instagram</div>
              <div className="text-xs text-zinc-500 truncate">{brandShort} auf Instagram</div>
            </div>
          </a>
        )}
        {fb && (
          <a href={fb} target="_blank" rel="noopener noreferrer"
             data-testid="portal-facebook-link"
             className="flex items-center gap-3 border border-zinc-800 rounded-lg p-4 bg-zinc-900/70 hover:border-cyan-400 transition-colors">
            <span className="w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0 bg-[#1877F2]">
              <FacebookIcon size={20} />
            </span>
            <div className="min-w-0">
              <div className="font-black uppercase tracking-wide text-sm truncate">Facebook</div>
              <div className="text-xs text-zinc-500 truncate">{brandShort} auf Facebook</div>
            </div>
          </a>
        )}
      </div>
    </div>
  );
}
